/**
 * Productive-timing client: the caller's "Best times" (per activity kind, the windows that suit
 * them in plain words, a reason, and a 24-hour strip) and the timing cues behind it. The server
 * builds everything in Rust (`fluxability_domain::suitability`); this module only fetches it and
 * deletes a cue through the shared insight route. Every call resolves to a {@link FluxAgentResult}.
 *
 * @module apps/web/src/lib/fluxAgentTimingApi
 */

import { request } from '@/lib/api';
import { toFluxAgentResult, type FluxAgentResult } from '@/lib/fluxAgentApi';

/** Coarse kinds of activity whose best timing differs from person to person (`ActivityKind`). */
export type FluxAgentActivityKind =
  | 'deep_work'
  | 'admin'
  | 'meetings'
  | 'creative'
  | 'physical'
  | 'errands'
  | 'learning'
  | 'rest';

/** One hour of the day for one kind, averaged over the week. */
export interface FluxAgentTimingHour {
  /** Suitability from -1 (worst) to 1 (best); 0 when unknown. */
  value: number;
  /** How much evidence backs it, 0-1. */
  confidence: number;
}

/** Best times for one activity kind. */
export interface FluxAgentTimingKind {
  /** Kind identifier. */
  kind: FluxAgentActivityKind;
  /** Display label such as "Deep work". */
  label: string;
  /** Strongest suitable windows in words, such as "weekday mornings" (at most two). */
  best: string[];
  /** Windows that suit this kind worst, in words (at most two). */
  harder: string[];
  /** A short reason for the strongest window, without numbers, or null with no positive evidence. */
  why: string | null;
  /** 24 entries, local midnight first. */
  hours: FluxAgentTimingHour[];
}

/** A stored timing cue the person can delete. */
export interface FluxAgentTimingCue {
  /** Insight id; delete with `DELETE /flux-agent/insights/:id`. */
  id: string;
  /** Conversation it was heard in, or null when that conversation was deleted. */
  conversationId: string | null;
  /** Kind identifier. */
  kind: FluxAgentActivityKind;
  /** Display label of the kind. */
  label: string;
  /** Windows in local time, such as "weekdays 07:00-09:00". */
  windows: string;
  /** -1 (worst) to 1 (best). */
  suitability: number;
  /** Confidence 0-1. */
  confidence: number;
  /** Paraphrase of what the person said. */
  basis: string;
  /** UTC creation timestamp as stored by D1. */
  createdAt: string;
}

/** The whole Best times view. */
export interface FluxAgentTiming {
  /** Whether conversation learning is on; when off, stored cues do not shape the map. */
  learningEnabled: boolean;
  /** One entry per kind, in a stable order. */
  kinds: FluxAgentTimingKind[];
  /** Stored cues, newest first. */
  cues: FluxAgentTimingCue[];
}

/** Productive-timing route methods; each resolves to a result and never throws for HTTP failures. */
export const fluxAgentTimingApi = {
  /** GET `/flux-agent/timing`: the caller's best times and stored cues. */
  getTiming: async (): Promise<FluxAgentResult<FluxAgentTiming>> =>
    toFluxAgentResult(await request<FluxAgentTiming>('/flux-agent/timing')),
  /** DELETE `/flux-agent/insights/:id`: forget one cue permanently. */
  deleteCue: async (id: string): Promise<FluxAgentResult<{ deleted: number }>> =>
    toFluxAgentResult(
      await request<{ deleted: number }>(`/flux-agent/insights/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      })
    ),
};
